// src/components/elements/KanbanBoard/CustomFieldSettings/CustomFieldSettings.tsx
import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Switch, 
  Space, 
  Typography, 
  Tag, 
  Tooltip, 
  Popconfirm,
  message
} from 'antd';
import { 
  PlusCircle, 
  Edit, 
  Trash2, 
  ArrowUp, 
  ArrowDown, 
  Type, 
  Hash, 
  Calendar, 
  List, 
  Check, 
  ExternalLink, 
  User
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../stores/root-store-context';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface FieldTypeOption {
  value: number;
  label: string;
  icon: React.ReactNode;
}

interface CustomField {
  id: string;
  name: string;
  description?: string;
  type: number;
  required: boolean;
  options?: string;
  showInCard: boolean;
  displayOrder: number;
  projectId: string;
}

interface CustomFieldsAdminProps {
  projectId: string;
}

export const CustomFieldsAdmin = observer(({ projectId }: CustomFieldsAdminProps) => {
  const { t } = useTranslation();
  const { kanbanTaskStore } = useStore();
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [loading, setLoading] = useState(true);

  const fieldTypeOptions: FieldTypeOption[] = [
    { value: 0, label: t('Text'), icon: <Type size={16} /> },
    { value: 1, label: t('Number'), icon: <Hash size={16} /> },
    { value: 2, label: t('Date'), icon: <Calendar size={16} /> },
    { value: 3, label: t('Dropdown'), icon: <List size={16} /> },
    { value: 4, label: t('Checkbox'), icon: <Check size={16} /> },
    { value: 5, label: t('MultiSelect'), icon: <List size={16} /> },
    { value: 6, label: t('URL'), icon: <ExternalLink size={16} /> },
    { value: 7, label: t('User'), icon: <User size={16} /> }
  ];

  const getFieldTypeLabel = (type: number): string => {
    const option = fieldTypeOptions.find(opt => opt.value === type);
    return option ? option.label : t('Unknown');
  };

  const getFieldTypeIcon = (type: number): React.ReactNode => {
    const option = fieldTypeOptions.find(opt => opt.value === type);
    return option ? option.icon : <Type size={16} />;
  };

  // Function to refresh fields data
  const refreshFields = async () => {
    setLoading(true);
    try {
      await kanbanTaskStore.getProjectCustomFields(projectId);
    } catch (error) {
      console.error("Error refreshing custom fields:", error);
      message.error(t("FailedToRefreshCustomFields"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshFields();
  }, [projectId, kanbanTaskStore]);

  const showModal = (field: CustomField | null = null) => {
    setEditingField(field);
    if (field) {
      form.setFieldsValue({
        id: field.id,
        name: field.name,
        description: field.description || '',
        type: field.type,
        required: field.required,
        options: field.options || '',
        showInCard: field.showInCard
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        type: 0,
        required: false,
        showInCard: false
      });
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingField(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingField) {
        // Update existing field
        const updatedField = {
          ...editingField,
          ...values
        };
        
        try {
          await kanbanTaskStore.updateCustomField(editingField.id, updatedField);
          // Success is determined by no exception being thrown
          message.success(t('CustomFieldUpdatedSuccessfully'));
          // Refresh the data after successful update
          await refreshFields();
        } catch (err) {
          console.error("Update error:", err);
          message.error(t('FailedToUpdateCustomField'));
          return; // Don't close modal on error
        }
      } else {
        // Create new field
        const newField = {
          ...values,
          projectId,
          displayOrder: kanbanTaskStore.projectCustomFields.length
        };
        
        const result = await kanbanTaskStore.createCustomField(newField);
        if (result) {
          message.success(t('CustomFieldCreatedSuccessfully'));
          // Refresh the data after successful creation
          await refreshFields();
        } else {
          message.error(t('FailedToCreateCustomField'));
          return; // Don't close modal on error
        }
      }
      
      setIsModalVisible(false);
      setEditingField(null);
      form.resetFields();
    } catch (error) {
      console.error("Error submitting form:", error);
      message.error(t('PleaseCheckFormFieldsAndTryAgain'));
    }
  };

  const handleDelete = async (fieldId: string) => {
    try {
      // For 204 responses, successful deletion is indicated by no error being thrown
      await kanbanTaskStore.deleteCustomField(fieldId);
      message.success(t('CustomFieldDeletedSuccessfully'));
      // Refresh the data after successful deletion
      await refreshFields();
    } catch (error) {
      console.error("Error deleting field:", error);
      message.error(t('FailedToDeleteCustomField'));
    }
  };

  const handleMoveUp = async (field: CustomField) => {
    if (field.displayOrder <= 0) return;
    
    try {
      // Implementation needed for order update
      // await kanbanTaskStore.updateCustomFieldOrder(field.id, field.displayOrder - 1);
      message.success(t('FieldOrderUpdated'));
      // Refresh after order change
      await refreshFields();
    } catch (error) {
      console.error("Error updating field order:", error);
      message.error(t('FailedToUpdateFieldOrder'));
    }
  };

  const handleMoveDown = async (field: CustomField) => {
    if (field.displayOrder >= kanbanTaskStore.projectCustomFields.length - 1) return;
    
    try {
      // Implementation needed for order update
      // await kanbanTaskStore.updateCustomFieldOrder(field.id, field.displayOrder + 1);
      message.success(t('FieldOrderUpdated'));
      // Refresh after order change
      await refreshFields();
    } catch (error) {
      console.error("Error updating field order:", error);
      message.error(t('FailedToUpdateFieldOrder'));
    }
  };

  const columns = [
    {
      title: t('FieldName'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: CustomField) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getFieldTypeIcon(record.type)}
          <span>{text}</span>
          {record.required && <span style={{ color: '#ff4d4f' }}>*</span>}
        </div>
      )
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: number) => (
        <Tag color="blue" className="field-type-tag">
          {getFieldTypeLabel(type)}
        </Tag>
      )
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: t('ShowInCard'),
      dataIndex: 'showInCard',
      key: 'showInCard',
      render: (showInCard: boolean) => (
        <Switch disabled checked={showInCard} size="small" />
      )
    },
    {
      title: t('Actions'),
      key: 'actions',
      render: (_: any, record: CustomField) => (
        <Space size="small">
          <Button 
            type="text" 
            icon={<Edit size={16} />} 
            onClick={() => showModal(record)}
            className="hover-effect"
          />
          <Popconfirm
            title={t('DeleteThisField')}
            description={t('DeleteFieldConfirmation')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('Yes')}
            cancelText={t('No')}
          >
            <Button 
              type="text" 
              danger 
              icon={<Trash2 size={16} />}
              className="hover-effect"
            />
          </Popconfirm>
          <Button
            type="text"
            icon={<ArrowUp size={16} />}
            onClick={() => handleMoveUp(record)}
            disabled={record.displayOrder <= 0}
            className="hover-effect"
          />
          <Button
            type="text"
            icon={<ArrowDown size={16} />}
            onClick={() => handleMoveDown(record)}
            disabled={record.displayOrder >= kanbanTaskStore.projectCustomFields.length - 1}
            className="hover-effect"
          />
        </Space>
      )
    }
  ];

  return (
    <div className="custom-fields-management">
      <div className="custom-fields-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <Title level={4}>{t('CustomFields')}</Title>
        <Button
          type="primary"
          icon={<PlusCircle size={16} />}
          onClick={() => showModal()}
          className="hover-effect"
        >
          {t('AddCustomField')}
        </Button>
      </div>

      <Table
        dataSource={kanbanTaskStore.projectCustomFields}
        columns={columns}
        rowKey="id"
        loading={loading}
        className="custom-fields-table"
        pagination={false}
      />

      <Modal
        title={editingField ? t('EditCustomField') : t('AddCustomField')}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {t('Cancel')}
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {editingField ? t('Update') : t('Create')}
          </Button>
        ]}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label={t('FieldName')}
            name="name"
            rules={[{ required: true, message: t('PleaseEnterFieldName') }]}
            className="custom-field-form-item"
          >
            <Input placeholder={t('EnterFieldName')} />
          </Form.Item>

          <Form.Item
            label={t('Description')}
            name="description"
            className="custom-field-form-item"
          >
            <TextArea
              placeholder={t('EnterFieldDescriptionOptional')}
              rows={2}
            />
          </Form.Item>

          <Form.Item
            label={t('FieldType')}
            name="type"
            rules={[{ required: true, message: t('PleaseSelectFieldType') }]}
            className="custom-field-form-item"
          >
            <Select placeholder={t('SelectFieldType')}>
              {fieldTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {option.icon}
                    <span>{option.label}</span>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.type !== currentValues.type
            }
          >
            {({ getFieldValue }) => {
              const fieldType = getFieldValue('type');
              if (fieldType === 3 || fieldType === 5) { // Dropdown or Multi-Select
                return (
                  <Form.Item
                    label={t('Options')}
                    name="options"
                    rules={[{ required: true, message: t('PleaseEnterOptions') }]}
                    extra={t('EnterOptionsSeparatedByCommas')}
                    className="custom-field-form-item"
                  >
                    <TextArea
                      placeholder={t('OptionsSeparatedByCommasExample')}
                      rows={3}
                    />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            label={t('Required')}
            name="required"
            valuePropName="checked"
            className="custom-field-form-item"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={t('ShowInKanbanCard')}
            name="showInCard"
            valuePropName="checked"
            tooltip={t('DisplayFieldInKanbanCard')}
            className="custom-field-form-item"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

export default CustomFieldsAdmin;